import type {
  EmbeddedNavigationGroup,
  EmbeddedNavigationItem,
} from '@/types/embeddedApplication';
import type {
  NativeNavigationItem,
  NavigationNode,
  NavigationRow,
} from '@/navigation/NavigationTypes';

function mapNodeToEmbeddedItem(node: NavigationNode): EmbeddedNavigationItem {
  return {
    id: node.id,
    label: node.label,
    icon: node.icon,
    iframeUrl: node.route,
    route: node.route,
    visible: node.visibility === 'visible',
    permissions: node.permissions,
    children: node.children.map(mapNodeToEmbeddedItem),
  };
}

function mapGroupNodeToEmbeddedGroup(
  group: NavigationNode,
): EmbeddedNavigationGroup {
  return {
    id: group.id,
    label: group.label,
    items: group.children.map((child) =>
      child.type === 'group'
        ? {
            ...mapNodeToEmbeddedItem(child),
            children: child.children.map(mapNodeToEmbeddedItem),
          }
        : mapNodeToEmbeddedItem(child),
    ),
    collapsible: true,
    defaultExpanded: group.visibility === 'visible',
    visible: group.visibility !== 'hidden',
    permissions: group.permissions,
  };
}

export function buildNavigationRows(
  nodes: NavigationNode[],
  expandedIds: Set<string>,
): NavigationRow[] {
  const rows: NavigationRow[] = [];

  const walk = (items: NavigationNode[], depth: number) => {
    items.forEach((node) => {
      rows.push({ node, depth });

      if (node.children.length > 0 && expandedIds.has(node.id)) {
        walk(node.children, depth + 1);
      }
    });
  };

  walk(nodes, 0);
  return rows;
}

export function filterNavigationRows(
  rows: NavigationRow[],
  query: string,
): NavigationRow[] {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return rows;
  }

  return rows.filter(({ node }) => {
    return (
      node.label.toLowerCase().includes(normalizedQuery) ||
      node.route?.toLowerCase().includes(normalizedQuery) ||
      node.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(normalizedQuery),
      )
    );
  });
}

export function navigationTreeToEmbeddedGroups(
  nodes: NavigationNode[],
): EmbeddedNavigationGroup[] {
  const groups: EmbeddedNavigationGroup[] = [];
  let pendingRootItems: EmbeddedNavigationItem[] = [];
  let rootSectionIndex = 0;

  const flushPendingRootItems = () => {
    if (pendingRootItems.length === 0) {
      return;
    }

    groups.push({
      id: `__root-navigation-items__-${rootSectionIndex}`,
      label: '',
      items: pendingRootItems,
      collapsible: false,
      defaultExpanded: true,
      visible: true,
    });

    rootSectionIndex += 1;
    pendingRootItems = [];
  };

  nodes.forEach((node) => {
    if (node.type === 'item') {
      pendingRootItems.push(mapNodeToEmbeddedItem(node));
      return;
    }

    flushPendingRootItems();
    groups.push(mapGroupNodeToEmbeddedGroup(node));
  });

  flushPendingRootItems();

  return groups;
}

export function collectEmbeddedNavigationItemIds(
  groups: EmbeddedNavigationGroup[],
): string[] {
  const collect = (items: EmbeddedNavigationItem[]): string[] =>
    items.flatMap((item) => [item.id, ...collect(item.children ?? [])]);

  return groups.flatMap((group) => collect(group.items));
}

function mapNodeToNativeNavigationItem(
  node: NavigationNode,
): NativeNavigationItem {
  return {
    id: node.id,
    label: node.label,
    route: node.route,
    icon: node.icon,
    visibility: node.visibility,
    openMode: node.openMode,
    permissions: node.permissions,
    children: node.children.map(mapNodeToNativeNavigationItem),
  };
}

export function navigationTreeToNativeItems(
  nodes: NavigationNode[],
): NativeNavigationItem[] {
  return nodes.map(mapNodeToNativeNavigationItem);
}
